import type { ColumnDefinition } from 'components/SubTable';
import type { PayoutPartyResponse } from 'payments-api/model';
import { useGetInvoicesSuspense } from 'api/payments/generated/fragmentPaymentsAPI';
import {
  PaymentViewHeader,
  PaymentViewLayout,
} from 'components/Layout/PaymentViewLayout';
import { SubTable } from 'components/SubTable';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import React, { Suspense, useMemo } from 'react';
import { makePath } from 'utils/routes';
import useLocation from 'wouter/use-location';

import { TertiaryButton } from '@fragment/ui/src/components/Button/TertiaryButton/TertiaryButton';
import { DetailField } from '@fragment/ui/src/components/DetailField/DetailField';
import { StatusBadge } from '@fragment/ui/src/components/StatusBadge/StatusBadge';

// Helper to check if payout party matches a given externalId
const isPayoutPartyMatch = (
  payoutParty: PayoutPartyResponse,
  externalId: string
): boolean => {
  if ('platform' in payoutParty && payoutParty.platform) {
    return false;
  }
  return 'party_id' in payoutParty && payoutParty.party_id === externalId;
};

// Helper to get display string for payout party
const getPayoutPartyDisplay = (payoutParty: PayoutPartyResponse): string => {
  if ('platform' in payoutParty && payoutParty.platform) {
    return 'Platform';
  }
  return 'party_id' in payoutParty ? payoutParty.party_id : 'Unknown';
};

type Props = {
  workspaceId: string;
  invoiceId: string;
  partyExternalId: string;
};

type InvoiceItem = {
  id: string;
  description: string;
  productId: string;
  payoutParty: string;
  amount: number | string;
};

const formatCurrency = (amount: number) =>
  `$${(amount / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const PublicInvoiceViewPageContent = ({
  workspaceId,
  invoiceId,
  partyExternalId,
}: Props): JSX.Element => {
  const { data } = useGetInvoicesSuspense();
  const [, setLocation] = useLocation();

  // Find the specific invoice by id
  const invoice = useMemo(() => {
    return data.data.find((inv) => inv.id === invoiceId);
  }, [data, invoiceId]);

  useDocumentTitle(invoice ? `Invoice ${invoice.ik}` : 'Invoice Detail');

  // Filter items to only include those involving the current party
  const partyItems = useMemo(() => {
    if (!invoice?.lineItems) return [];
    // Check if party is the buyer or one of the sellers
    return invoice.lineItems.filter(
      (item) =>
        invoice.buyerParty === partyExternalId ||
        isPayoutPartyMatch(item.payout_party, partyExternalId)
    );
  }, [invoice, partyExternalId]);

  // Calculate total from items involving the current party
  const total = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      partyItems.reduce(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (sum: number, item: any) => sum + Number(item.amount),
        0
      ),
    [partyItems]
  );

  // Prepare items data for SubTable
  const items: InvoiceItem[] = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      partyItems.map((item: any) => ({
        id: item.id,
        description: item.description,
        productId: item.product_id,
        payoutParty: getPayoutPartyDisplay(item.payout_party),
        amount: item.amount,
      })),
    [partyItems]
  );

  // Define schema for items table
  const itemsSchema: readonly ColumnDefinition<InvoiceItem>[] = useMemo(
    () => [
      {
        title: 'Description',
        format: (item: InvoiceItem) => item.description,
      },
      {
        title: 'Seller',
        format: (item: InvoiceItem) => (
          <span className="text-main-500">{item.payoutParty}</span>
        ),
      },
      {
        title: 'Amount',
        justify: 'right' as const,
        format: (item: InvoiceItem) => formatCurrency(Number(item.amount)),
      },
    ],
    []
  );

  const handleItemClick = (item: InvoiceItem) => {
    // Future: navigate to item detail or show item details
    // eslint-disable-next-line no-console
    console.log('Clicked item:', item);
  };

  const backLink = makePath(
    ['w', workspaceId, 'payments', 'parties', partyExternalId, 'view'],
    {
      absolute: true,
    }
  );

  if (!invoice) {
    return (
      <PaymentViewLayout
        header={
          <PaymentViewHeader>
            <div className="flex justify-between w-full">
              <h1 className="text-xl font-main">{partyExternalId} Portal</h1>
              <TertiaryButton onClick={() => setLocation(backLink)}>
                Go back
              </TertiaryButton>
            </div>
          </PaymentViewHeader>
        }
      >
        <div className="flex items-center justify-center h-full">
          <div className="text-main-500 text-main">Invoice not found</div>
        </div>
      </PaymentViewLayout>
    );
  }

  return (
    <PaymentViewLayout
      header={
        <PaymentViewHeader>
          <div className="flex justify-between w-full h-full">
            <h1 className="text-xl font-main">{partyExternalId} Portal</h1>
            <TertiaryButton onClick={() => setLocation(backLink)}>
              Go back
            </TertiaryButton>
          </div>
        </PaymentViewHeader>
      }
    >
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col w-full">
          {/* Invoice header with name and status */}
          <DetailField label="Name" className="w-full" padding={false}>
            <div className="flex w-full justify-between h-f8">
              <div className="text-lg p-f1">{invoice.ik}</div>
              <StatusBadge
                className="h-full capitalize border-0 border-l-[0.5px]"
                status={
                  invoice.status as
                    | 'open'
                    | 'closed'
                    | 'draft'
                    | 'active'
                    | 'void'
                    | 'failed'
                }
              />
            </div>
          </DetailField>

          {/* Total field */}
          <DetailField label="Amount">{formatCurrency(total)}</DetailField>

          {/* Date field */}
          <DetailField label="Issued">
            {formatDate(invoice.created)}
          </DetailField>

          <div className="h-f4" />

          {/* Items section */}
          <DetailField
            label="Items"
            padding={false}
            className="border-t-[0.5px]"
          >
            <div className="w-full">
              <SubTable
                data={items}
                schema={itemsSchema}
                onClick={handleItemClick}
                textSize="md"
                showHeader
                stickyHeader={false}
                emptyValue={{
                  Description: <span className="text-main-500">No items</span>,
                  Seller: '',
                  Amount: '',
                }}
              />
            </div>
          </DetailField>
        </div>
      </div>
    </PaymentViewLayout>
  );
};

export const PublicInvoiceViewPage = ({
  workspaceId,
  invoiceId,
  partyExternalId,
}: Props): JSX.Element => (
  <Suspense
    fallback={
      <PaymentViewLayout
        header={
          <PaymentViewHeader>
            <div className="flex justify-between w-full">
              <h1 className="text-xl font-main">{partyExternalId} Portal</h1>
            </div>
          </PaymentViewHeader>
        }
      >
        <div className="flex items-center justify-center h-full">
          <div className="text-main-500 text-main">Loading invoice...</div>
        </div>
      </PaymentViewLayout>
    }
  >
    <PublicInvoiceViewPageContent
      workspaceId={workspaceId}
      invoiceId={invoiceId}
      partyExternalId={partyExternalId}
    />
  </Suspense>
);
